import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { lawnServiceProviderInfoService } from '../../../../services/lawnServiceProviderInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { useUserSelection } from '../../../../contexts/UserSelectionContext';
const logger = createLogger('LawnServiceProviderData');

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || 'Not specified'}</Text>
    <View style={styles.separator} />
  </View>
);

export const LawnServiceProviderDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<{ params: RouteParams }, 'params'>>();
  const { targetUserId, viewOnly } = route.params || {};
  // K5: a successor sees the owner's logins once the death is verified.
  const { isDeathVerifiedForSelectedUser } = useUserSelection();
  const [lawnServiceProviderData, setLawnServiceProviderData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUsername, setShowUsername] = useState<{ [key: string]: boolean }>({});
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadLawnServiceProviderData();
  }, []);

  const loadLawnServiceProviderData = async () => {
    try {
      setLoading(true);
      const data = await lawnServiceProviderInfoService.getAllLawnServiceProviderInfo(targetUserId);
      
      if (data && data.length > 0) {
        // Skip loading portal credentials in viewOnly mode - they are protected until EOL
        if (!viewOnly || isDeathVerifiedForSelectedUser) {
          // Load portal credentials for each lawn service provider
          const updatedData = await Promise.all(
            data.map(async (lawnServiceProvider) => {
              try {
                logger.debug('[Portal Debug] Attempting to load portal credentials for lawn service provider ID:', lawnServiceProvider.id);
                const portalData = await vaultService.getPortalCredentials(lawnServiceProvider.id, 'lawn-service-provider');
                logger.debug('[Portal Debug] Portal data retrieved for lawn service provider:', lawnServiceProvider.id, portalData);
                if (portalData) {
                  lawnServiceProvider.username = portalData.username;
                  lawnServiceProvider.password = portalData.password;
                  lawnServiceProvider.accountNumber = portalData.accountNumber;
                  logger.debug('[Portal Debug] Assigned credentials to lawn service provider data');
                } else {
                  logger.debug('[Portal Debug] No portal data returned for lawn service provider:', lawnServiceProvider.id);
                }
              } catch (vaultError) {
                logger.debug('[Portal Debug] Error loading portal data for lawn service provider:', lawnServiceProvider.id, vaultError);
              }
              return lawnServiceProvider;
            })
          );

          logger.debug('[Data Debug] Final lawn service provider data being set:', updatedData.length, 'providers');
          logger.debug('[Data Debug] First provider account number:', updatedData[0]?.accountNumber);
          setLawnServiceProviderData(updatedData);
        } else {
          setLawnServiceProviderData(data);
        }
      }
    } catch (error) {
      logger.error('Error loading lawn service provider data:', error);
      Alert.alert('Error', 'Failed to load lawn service provider information');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatCurrency = (amount: string | number) => {
    if (!amount) return '';
    if (typeof amount === 'number') {
      return `$${amount.toFixed(2)}`;
    }
    return amount.toString();
  };

  const handleEditLawnServiceProvider = (lawnServiceProvider: any) => {
    navigation.navigate('LawnServiceProviderInput', { 
      lawnServiceProviderId: lawnServiceProvider.id, 
      isEdit: true 
    });
  };

  const handleDeleteLawnServiceProvider = async (lawnServiceProvider: any) => {
    Alert.alert('Delete', 'Are you sure you want to delete this lawn service provider information?', [
      { text: 'Cancel', style: 'cancel' },
      { 
        text: 'Delete', 
        style: 'destructive', 
        onPress: async () => {
          try {
            await lawnServiceProviderInfoService.deleteLawnServiceProviderInfo(lawnServiceProvider.id);
            
            // Reload data after deletion
            await loadLawnServiceProviderData();
            
            Alert.alert('Deleted', 'Lawn service provider information deleted.');
          } catch (e) {
            logger.error('Delete lawn service provider info failed:', e);
            Alert.alert('Error', 'Failed to delete lawn service provider information');
          }
        } 
      },
    ]);
  };

  const handleAddLawnServiceProvider = () => {
    navigation.navigate('LawnServiceProviderInput');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Lawn Service Provider Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading lawn service provider information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!lawnServiceProviderData || lawnServiceProviderData.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Lawn Service Provider Information</Text>

        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}

        <View style={styles.whiteContainer}>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              {viewOnly
                ? "This user has not added lawn service information yet."
                : "No lawn service provider information found"}
            </Text>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addButton}
                onPress={handleAddLawnServiceProvider}
              >
                <Text style={styles.addButtonText}>Add Lawn Service Provider Info</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Lawn Service Provider Information</Text>

      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {lawnServiceProviderData.map((lawnServiceProvider, index) => (
            <View key={lawnServiceProvider.id || index} style={styles.lawnServiceProviderCard}>
              <View style={styles.lawnServiceProviderHeader}>
                <View style={{flex: 1, flexShrink: 1}}>
                  <Text style={styles.lawnServiceProviderName}>
                    {lawnServiceProvider.companyName || 'Unknown Provider'}
                  </Text>
                  <Text style={styles.lawnServiceProviderType}>
                    {lawnServiceProvider.serviceFrequency || 'Not specified'}
                  </Text>
                </View>
                {!viewOnly && (
                  <View style={styles.cardButtons}>
                    <TouchableOpacity
                      style={styles.editLawnServiceProviderButton}
                      onPress={() => handleEditLawnServiceProvider(lawnServiceProvider)}
                    >
                      <Text style={styles.editLawnServiceProviderButtonText}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteLawnServiceProviderButton}
                      onPress={() => handleDeleteLawnServiceProvider(lawnServiceProvider)}
                    >
                      <Text style={styles.deleteLawnServiceProviderButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
              
              <InfoItem label="Company Name" value={lawnServiceProvider.companyName || ''} />
              <InfoItem label="Contact Person" value={lawnServiceProvider.contactPerson || ''} />
              <InfoItem label="Phone Number" value={formatPhoneNumber(lawnServiceProvider.phoneNumber) || ''} />
              <InfoItem label="Email" value={lawnServiceProvider.email || ''} />
              <InfoItem label="Service Address" value={lawnServiceProvider.serviceAddress || ''} />
              <InfoItem label="Services Provided" value={lawnServiceProvider.servicesProvided || ''} />
              <InfoItem label="Service Schedule" value={lawnServiceProvider.serviceSchedule || ''} />
              <InfoItem label="Cost Per Service" value={formatCurrency(lawnServiceProvider.costPerService) || ''} />
              <InfoItem label="Annual Contract" value={lawnServiceProvider.annualContract ? 'Yes' : 'No'} />
              <InfoItem label="Contract Start Date" value={lawnServiceProvider.contractStartDate || ''} />
              <InfoItem label="Contract End Date" value={lawnServiceProvider.contractEndDate || ''} />
              <InfoItem label="Seasonal Service" value={lawnServiceProvider.seasonalService ? 'Yes' : 'No'} />
              <InfoItem label="Emergency Contact" value={lawnServiceProvider.emergencyContact || ''} />
              <InfoItem label="Special Instructions" value={lawnServiceProvider.specialInstructions || ''} />

              {lawnServiceProvider.username && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Portal Username</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showUsername[lawnServiceProvider.id] ? lawnServiceProvider.username : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowUsername(prev => ({ ...prev, [lawnServiceProvider.id]: !prev[lawnServiceProvider.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showUsername[lawnServiceProvider.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}

              {lawnServiceProvider.password && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Portal Password</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showPassword[lawnServiceProvider.id] ? lawnServiceProvider.password : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowPassword(prev => ({ ...prev, [lawnServiceProvider.id]: !prev[lawnServiceProvider.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showPassword[lawnServiceProvider.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              <InfoItem label="Notes" value={lawnServiceProvider.notes || ''} />
            </View>
          ))}

          <View style={styles.buttonContainer}>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addAnotherLawnServiceProviderButton}
                onPress={handleAddLawnServiceProvider}
              >
                <Text style={styles.addAnotherLawnServiceProviderButtonText}>Add Another Lawn Service Provider</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.goBack()}
            >
              <Text style={styles.buttonText}>Back to Checklist</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  lawnServiceProviderCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  lawnServiceProviderHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(2),
    borderBottomColor: '#29BFC0',
  },
  lawnServiceProviderName: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  lawnServiceProviderType: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  cardButtons: {
    flexDirection: 'row',
    gap: ms(8),
  },
  editLawnServiceProviderButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  editLawnServiceProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  deleteLawnServiceProviderButton: {
    backgroundColor: '#DC3545',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  deleteLawnServiceProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyeButton: {
    padding: ms(5),
    marginLeft: ms(10),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  separator: {
    height: s(1),
    backgroundColor: '#E5E5E5',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
    gap: ms(15),
  },
  addAnotherLawnServiceProviderButton: {
    backgroundColor: '#F2994A',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherLawnServiceProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  button: {
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});